import {
  CompanyFormData,
  MAX_SIZE,
  SUPPORTED_FORMATS,
} from "Components/Companies/CompanyEdit/CompanyEditForm";
import { StyledError } from "Components/Companies/CompanyEdit/CompanyEditBasicInfo/CompanyEditBasicInfoStyles";
import * as React from "react";
import { UseFormMethods, FieldError } from "react-hook-form";
import { Resources, useResource } from "Translations/Resources";
import { Typography } from "@material-ui/core";
import { setDisableRedirect } from "State/App/AppReducer";
import { useDispatch } from "react-redux";
import { nameof } from "Utils/ObjectUtils";
import styled from "styled-components";
import InputLabel from "@material-ui/core/InputLabel";
import { DataImage } from "Api/Api";
import { TextInput } from "Components/Shared/Inputs/TextInput";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { SecondaryButton } from "Components/Shared/Buttons/SecondaryButton";

const StyledWrapper = styled.div<{
  signaturePhotoUrl: boolean;
  hasError: boolean;
}>`
  display: grid;
  grid-template-columns: minmax(auto, 450px) auto 1fr;
  gap: ${props => props.theme.spacing(1, 3)};
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(5)}px;

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .file-input {
    position: static;

    .ibis-MuiInputBase-root {
      position: static;
    }

    fieldset {
      display: none;
    }

    input {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      z-index: 100;
      opacity: 0;
      visibility: hidden;
      padding: 0;
    }

    &-wrapper {
      position: relative;
      cursor: pointer;
      display: inline-flex;
      align-items: center;

      ${props =>
        !props.signaturePhotoUrl &&
        `border: 1px solid ${props.theme.colors.border}; 
        border-radius: ${props.theme.borderRadius}; 
        height: 38px;
        max-width: 100%;`}

      ${props =>
        props.hasError &&
        `border-color: ${props.theme.colors.red}`}

      .ibis-MuiButtonBase-root {
        width: auto;
      }
    }

    &-img {
      max-height: 200px;
      display: inline-flex;

      img {
        max-width: 100%;
        max-height: 200px;
      }
    }

    &-label {
      padding: 0 15px;
      white-space: nowrap;

      &.selected {
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 17px;
      }
    }
  }

  .input-wrapper {
    position: absolute;
    top: -2px;
    left: 0;
    right: 0;
    bottom: -2px;
  }

  .error-image {
    grid-area: 1/3;
  }

  .btn-delete {
    margin-top: 5px;
  }
`;

type Props = {
  form: UseFormMethods<CompanyFormData>;
  photoUrlField: keyof CompanyFormData;
  imageField: keyof CompanyFormData;
  label: string;
};

export const CompanyEditImage: React.FunctionComponent<Props> = props => {
  const { register, watch, setValue, clearErrors, errors } = props.form;
  const { t } = useResource();
  const dispatch = useDispatch();
  const formValues = watch();
  let photoUrl = formValues[props.photoUrlField] as string;
  let image = formValues[props.imageField] as DataImage;

  React.useEffect(() => {
    register({ name: nameof<CompanyFormData>(props.photoUrlField) });
    register({ name: nameof<CompanyFormData>(props.imageField) });
  }, [register, props.photoUrlField, props.imageField]);

  const convertArrayBufferToBase64 = (
    arrayBuffer: string | ArrayBuffer | null,
    file: File,
  ) => {
    if (arrayBuffer) {
      const buffer = Buffer.from(arrayBuffer);
      const base64String = buffer.toString("base64");
      const imageData = {
        base64: base64String,
        name: file.name,
        contentType: file.type,
        size: file.size,
      } as DataImage;
      setValue(nameof<CompanyFormData>(props.imageField), imageData);
    }
  };

  const readSelectedFile = (files: FileList) => {
    const reader = new FileReader();
    reader.onload = () => {
      const arrayBuffer = reader.result;
      convertArrayBufferToBase64(arrayBuffer, files[0]);
      handleValidation(files[0]);
    };
    reader.readAsArrayBuffer(files[0]);
  };

  const handleFile = (files: FileList) => {
    setValue(nameof<CompanyFormData>(props.photoUrlField), undefined);

    if (files.length === 0) {
      setValue(nameof<CompanyFormData>(props.imageField), undefined);
      clearErrors(nameof<CompanyFormData>(props.imageField));
      return;
    }

    readSelectedFile(files);
  };

  const onChangeImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event?.currentTarget?.files ?? new FileList();
    handleFile(files);
    dispatch(setDisableRedirect(true));
  };

  const onClick = () => {
    setValue(nameof<CompanyFormData>(props.photoUrlField), undefined);
    setValue(nameof<CompanyFormData>(props.imageField), undefined);
    dispatch(setDisableRedirect(true));
  };

  const handleValidation = (file: File) => {
    if (isValidSize(file.size) || isValidFileExtension(file.name)) {
      clearErrors(nameof<CompanyFormData>(props.imageField));
    }
  };

  const isValidSize = (size: number) => size > MAX_SIZE;

  const isValidFileExtension = (name: string) =>
    SUPPORTED_FORMATS.filter(extension =>
      name.toLowerCase().includes(extension),
    ).length !== 0;

  return (
    <>
      <div className={"form-title"}>{props.label}</div>
      <StyledWrapper
        signaturePhotoUrl={!!photoUrl}
        hasError={!!errors[props.imageField]}
      >
        <div>
          <InputLabel
            className={"file-input-wrapper"}
            htmlFor={`file-upload-${props.imageField}`}
          >
            {photoUrl ? (
              <div className={"file-input-img"}>
                <img
                  src={photoUrl}
                  alt={t(Resources.CompanyEdit.Image.Label)}
                />
              </div>
            ) : image ? (
              <span className={"file-input-label selected"}>
                {t(Resources.CompanyEdit.Image.Input.Selected)} {image.name}
              </span>
            ) : (
              <>
                <span className={"file-input-label"}>
                  {t(Resources.CompanyEdit.Image.Input.Label)}
                </span>
                <PrimaryButton type={"button"}>
                  {t(Resources.CompanyEdit.Image.Btn.Select)}
                </PrimaryButton>
              </>
            )}
            <TextInput
              id={`file-upload-${props.imageField}`}
              type="file"
              name={props.imageField}
              className={"file-input"}
              onChange={onChangeImage}
            />
          </InputLabel>
          {photoUrl && (
            <div>
              <SecondaryButton
                type={"button"}
                className={"btn-delete"}
                onClick={_ => onClick()}
              >
                {t(Resources.CompanyEdit.Image.Btn.Delete)}
              </SecondaryButton>
            </div>
          )}
        </div>
        {errors[props.imageField] && (
          <StyledError className={"error-image"}>
            <Typography>
              {(errors[props.imageField] as FieldError).message}
            </Typography>
          </StyledError>
        )}
      </StyledWrapper>
    </>
  );
};
